import { createSlice } from "@reduxjs/toolkit";

import {
  getClassYears,
  getClasses,
  getClassesOfAcademicYear,
  getClassPublishReadinessOfAcademicYear,
  createClass,
  updateClass,
  deleteClass,
} from "./classAction";
import { ClassSortValue } from "config/constants";
import type { Class } from "types/Class";
import { getCurrentSchoolYear } from "utilities/CurrentSchoolYear";

interface classState {
  loading: boolean;
  classes: Class[];
  years: number[];
  yearsLoading: boolean;
  viewedYear: number;
  sortBy: ClassSortValue;
  error: string;
  success: boolean;
}

const initialState: classState = {
  loading: false,
  classes: [],
  years: [],
  yearsLoading: false,
  viewedYear: getCurrentSchoolYear(),
  sortBy: ClassSortValue.name_asc,
  error: "",
  success: false,
};

// The years are only fetched when YearButton mounts, so a class saved in a
// year that isn't listed yet (e.g. a facility's first class) adds it here.
const addYear = (state: classState, year: number | undefined) => {
  if (year !== undefined && !state.years.includes(year)) {
    state.years.push(year);
  }
};

const classSlice = createSlice({
  name: "class",
  initialState,
  reducers: {
    setSortBy: (state, action: { payload: { sortBy: ClassSortValue } }) => {
      state.sortBy = action.payload.sortBy;
    },
    setViewYear: (state, action: { payload: { year: number } }) => {
      state.viewedYear = action.payload.year;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getClassYears.pending, (state) => {
      state.yearsLoading = true;
    });
    builder.addCase(getClassYears.fulfilled, (state, { payload }) => {
      state.years = payload.years;
      state.yearsLoading = false;
    });
    builder.addCase(getClassYears.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.yearsLoading = false;
    });
    builder.addCase(getClasses.pending, (state) => {
      state.loading = true;
      state.success = false;
      state.classes = [];
    });
    builder.addCase(getClasses.fulfilled, (state, { payload }) => {
      state.classes = payload.classes;
      state.success = true;
      state.loading = false;
    });
    builder.addCase(getClasses.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(getClassesOfAcademicYear.pending, (state) => {
      state.loading = true;
      state.success = false;
      state.classes = [];
    });
    builder.addCase(
      getClassesOfAcademicYear.fulfilled,
      (state, { payload }) => {
        state.classes = payload.classes;
        state.success = true;
        state.loading = false;
      },
    );
    builder.addCase(getClassesOfAcademicYear.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(getClassPublishReadinessOfAcademicYear.pending, (state) => {
      state.loading = true;
      state.success = false;
      state.classes = [];
    });
    builder.addCase(
      getClassPublishReadinessOfAcademicYear.fulfilled,
      (state, { payload }) => {
        state.classes = payload.classes;
        state.success = true;
        state.loading = false;
      },
    );
    builder.addCase(
      getClassPublishReadinessOfAcademicYear.rejected,
      (state, { payload }) => {
        state.error = payload || "_generic._unknown_error";
        state.loading = false;
      },
    );
    builder.addCase(createClass.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(createClass.fulfilled, (state, { payload }) => {
      state.classes = [...state.classes, payload.class];
      addYear(state, payload.class.year);
      state.success = true;
      state.loading = false;
    });
    builder.addCase(createClass.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(updateClass.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(updateClass.fulfilled, (state, { payload }) => {
      addYear(state, payload.class.year);
      state.success = true;
      state.loading = false;
      const index = state.classes.findIndex((c) => c.id === payload.class.id);
      if (index >= 0) {
        state.classes[index] = {
          ...state.classes[index],
          ...payload.class,
        };
      }
    });
    builder.addCase(updateClass.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(deleteClass.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(deleteClass.fulfilled, (state, { payload }) => {
      state.success = true;
      state.loading = false;
      const index = state.classes.findIndex((c) => c.id === payload.id);
      if (index >= 0) state.classes.splice(index, 1);
    });
    builder.addCase(deleteClass.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
  },
});

export default classSlice.reducer;
export const { setSortBy, setViewYear } = classSlice.actions;
